import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.loversconnectmw.com";

const AUTH_STORAGE_KEY = "loversconnect_admin_auth";

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
});

let authSession = readStoredAuth();

client.interceptors.request.use((config) => {
  const token = authSession?.access_token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "Request failed";
    const wrapped = new Error(
      Array.isArray(message) ? message.join(", ") : message,
    );
    wrapped.status = error.response?.status;
    throw wrapped;
  },
);

function readStoredAuth() {
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function storeAuth(session) {
  authSession = session;
  if (session) {
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  } else {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
  }
}

function isPlaceholderName(name) {
  const normalized = (name || "").trim().toLowerCase();
  return !normalized || normalized === "lero user" || normalized === "unknown";
}

function displayNameFor(user) {
  if (!isPlaceholderName(user.name)) return user.name.trim();
  if (user.phone) return `Phone user ${user.phone}`;
  if (user.email && !user.email.endsWith("@otp.loversconnect.app"))
    return user.email;
  return user.email || "Unknown user";
}

function normalizeUser(user) {
  return {
    ...user,
    photo: user.primaryPhoto || user.photos?.[0] || null,
    displayName: user.displayName || displayNameFor(user),
    authMethod:
      user.authMethod ||
      (user.email?.endsWith("@otp.loversconnect.app") ? "OTP" : "EMAIL"),
    activeSessionCount: Number(user.activeSessionCount || 0),
    activeDeviceCount: Number(user.activeDeviceCount || 0),
    reportsAgainstCount: Number(
      user.reportsAgainstCount || user.counts?.reportsAgainst || 0,
    ),
    reportsFiledCount: Number(
      user.reportsFiledCount || user.counts?.reportsFiled || 0,
    ),
    swipesSentCount: Number(
      user.swipesSentCount || user.counts?.swipesSent || 0,
    ),
    swipesReceivedCount: Number(
      user.swipesReceivedCount || user.counts?.swipesReceived || 0,
    ),
    blocksReceivedCount: Number(
      user.blocksReceivedCount || user.counts?.blocksReceived || 0,
    ),
    blocksInitiatedCount: Number(
      user.blocksInitiatedCount || user.counts?.blocksInitiated || 0,
    ),
  };
}

const adminService = {
  getStoredAuth: () => authSession,

  logout: () => {
    storeAuth(null);
  },

  login: async (email, password) => {
    const { data: tokens } = await client.post("/auth/login", {
      email: email.trim(),
      password,
    });

    storeAuth(tokens);
    const { data: user } = await client.get("/users/me");

    if (!user?.isAdmin) {
      storeAuth(null);
      throw new Error("This account is not an administrator.");
    }

    const session = {
      ...tokens,
      user,
    };
    storeAuth(session);
    return session;
  },

  getOverviewStats: async () => {
    const { data } = await client.get("/users/admin/overview");
    return data;
  },

  sendSubscriptionReminders: async ({ title = "", body = "" } = {}) => {
    const { data } = await client.post(
      "/users/admin/subscription-reminders",
      {
        title: title.trim() || undefined,
        body: body.trim() || undefined,
      },
      { timeout: 120000 },
    );
    return data;
  },

  getUsers: async ({
    page = 1,
    limit = 25,
    status = "all",
    search = "",
    countryOfResidence = "",
  } = {}) => {
    const { data } = await client.get("/users/admin/users", {
      params: {
        page,
        limit,
        status,
        search: search.trim() || undefined,
        countryOfResidence: countryOfResidence.trim() || undefined,
      },
    });
    return {
      data: (data.data || []).map(normalizeUser),
      pagination: data.pagination || {
        page,
        limit,
        total: data.data?.length || 0,
        totalPages: 1,
      },
    };
  },

  getUser: async (userId) => {
    const { data } = await client.get(`/users/admin/users/${userId}`);
    return normalizeUser(data);
  },

  getReports: async (status = "open") => {
    const { data } = await client.get("/users/admin/reports", {
      params: { status },
    });
    return data;
  },

  resolveReport: async (reportId, status = "resolved", note = "") => {
    const { data } = await client.post(
      `/users/admin/reports/${reportId}/resolve`,
      {
        status,
        note,
      },
    );
    return data;
  },

  banUser: async (userId, reason = "Violation of terms") => {
    const { data } = await client.post("/users/admin/ban", { userId, reason });
    return data;
  },

  unbanUser: async (userId) => {
    const { data } = await client.post("/users/admin/unban", { userId });
    return data;
  },
};

export default adminService;
